import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook to detect when user scrolls to bottom of a container
 * Triggers loadMore callback when sentinel element becomes visible
 *
 * @param loadMore - Function to call when more data should be loaded
 * @param hasMore - Whether there are more items to load
 * @param loading - Whether data is currently being loaded
 * @returns ref to attach to the sentinel element
 */
export const useInfiniteScroll = (
    loadMore: () => void,
    hasMore: boolean,
    loading: boolean
) => {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const [sentinelElement, setSentinelElement] = useState<HTMLDivElement | null>(null);

    // Callback ref to track when sentinel element is mounted
    const setSentinelRef = useCallback((node: HTMLDivElement | null) => {
        sentinelRef.current = node;
        setSentinelElement(node);
    }, []);

    useEffect(() => {
        // Don't set up observer if sentinel element doesn't exist yet
        if (!sentinelElement) {
            return;
        }

        // Clean up previous observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        // Create new observer
        observerRef.current = new IntersectionObserver(
            (entries) => {
                // When sentinel element is visible and we have more data and not currently loading
                if (entries[0].isIntersecting && hasMore && !loading) {
                    loadMore();
                }
            },
            {
                threshold: 0.1, // Trigger when 10% of sentinel is visible
                rootMargin: '100px' // Start loading 100px before reaching sentinel
            }
        );

        // Start observing sentinel element
        observerRef.current.observe(sentinelElement);

        // Cleanup
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [sentinelElement, loadMore, hasMore, loading]);

    return setSentinelRef;
};
